import type { ModuleStore, StoreSubscribeListener } from '../types';
import type { Unsubscribe } from '../rtk-types';

import { createListenerCollection } from './createBatchedSubscription';

/**
 * Глобальная переменная, отвечающая за набор сторов в текущем вызове селектора (включая все его input-селекторы).
 * Актуальное значение гарантируется тем, что селектор синхронный и все его input-селекторы будут вызваны сразу же.
 */
let currentSelectorStores: Set<ModuleStore> | null = null;

/**
 * Добавляет стор, в список сторов, задействованных в текущем вызове селектора.
 * @param store Стор, который нужно добавить
 */
export function addCurrentSelectorStore(store: ModuleStore) {
    if (currentSelectorStores) {
        currentSelectorStores.add(store);
    }
}

/**
 * Создает функцию подписки, её актуализации и отписки на изменения в сторе селектора и сторах его input-селекторов.
 * Добавляет в селектор возможность подписки на изменения.
 * Возвращает:
 *   * `select` – функция для вызова селектора.
 *   * `subscribeToSelector` – функция для подписки на изменения в возвращаемом значении селектора
 *   * `subscribeToStores` – функция для инициализации физических подписок на задействованные Redux-сторы
 *   * `isStoresChangedSinceLastSelect` – функция, возвращающая `true` если с последнего вызова `select` значения в
 *      сторах уже изменились. Это возможно если подписка еще не создавалась, или `select` вызывается вне `subscribe`
 *
 * @param selector Селектор
 * @returns Функции `subscribe`, `select`, `setupSubscriptions`
 */
export function createSubscribableSelector<Args extends unknown[], Returned>(
    selector: (...args: Args) => Returned,
    { preserveSubscriptionFor }: { preserveSubscriptionFor?: ModuleStore } = {}
) {
    const listeners = createListenerCollection();
    const unsubscribeFns = new Map<ModuleStore, Unsubscribe>();
    let storesSnapshots: Array<[ModuleStore, ReturnType<ModuleStore['getState']>]> = [];

    function updateSnapshots(stores: Iterable<ModuleStore>) {
        storesSnapshots = [];

        for (const store of stores) {
            storesSnapshots.push([store, store.getState()]);
        }
    }

    function subscribeStores(storesToSubscribe: Iterable<ModuleStore>) {
        for (const storeToSubscribe of storesToSubscribe) {
            subscribeStore(storeToSubscribe);
        }
    }

    function subscribeStore(store: ModuleStore) {
        unsubscribeFns.set(store, store.subscribeBatched(listeners.notify));
    }

    let isSettedUp = false;
    let storesToSetupSubscriptions = new Set<ModuleStore>();

    function updateStoresSubscriptions(actualStores: Set<ModuleStore>) {
        // Если метод setupSubscriptions еще не был вызван, то ни одной подписки не создано и обновлять их не нужно
        // Сохраняем список задействованных в селекторе сторов, чтобы подписаться на них в setupStores позднее
        if (!isSettedUp) {
            updateSnapshots(actualStores);
            storesToSetupSubscriptions = actualStores;

            return;
        }

        // Отписываемся от тех сторов, которых не стало.
        for (const [store, unsubscribe] of unsubscribeFns.entries()) {
            if (!actualStores.has(store) && store !== preserveSubscriptionFor) {
                unsubscribe();
                unsubscribeFns.delete(store);
            }
        }

        // Подписываемся на те сторы, которых раньше не было.
        for (const store of actualStores) {
            if (!unsubscribeFns.has(store)) {
                subscribeStore(store);
            }
        }

        updateSnapshots(unsubscribeFns.keys());
    }

    return {
        subscribeToSelector(listener: StoreSubscribeListener) {
            return listeners.subscribe(listener);
        },

        select(...args: Args) {
            /**
             * При очередном вызове селектора накапливаем актуальный список сторов.
             */
            currentSelectorStores = new Set();

            try {
                /**
                 * Каждый селектор записывает свой стор в глобальный список сторов для текущего вызова.
                 * Актуальность и корректность этого списка гарантируется тем, что селектор синхронный и
                 * все его input-селекторы будут вызваны сразу же.
                 */
                const selected = selector.apply(null, args);

                // После вызова селектора новый список сторов накоплен. Обновляем подписки.
                updateStoresSubscriptions(currentSelectorStores);

                return selected;
            } finally {
                currentSelectorStores = null;
            }
        },

        subscribeToStores() {
            if (preserveSubscriptionFor) {
                storesToSetupSubscriptions.add(preserveSubscriptionFor);
            }

            if (!isSettedUp) {
                subscribeStores(storesToSetupSubscriptions);
            }

            isSettedUp = true;
            storesToSetupSubscriptions.clear();

            return () => {
                for (const unsubscribeFn of unsubscribeFns.values()) {
                    unsubscribeFn();
                }

                isSettedUp = false;
                unsubscribeFns.clear();
            };
        },

        isStoresChangedSinceLastSelect() {
            for (const snapshot of storesSnapshots) {
                if (snapshot[0].getState() !== snapshot[1]) {
                    return true;
                }
            }

            return false;
        },
    };
}
